import React, { useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { useToast } from '@/shared/hooks/use-toast';
import { useMeetingMutations, useMeetingProjectUsers } from '../hooks';
import type {
  Meeting,
  MeetingDraft,
  MeetingStatus,
  MeetingType,
  UserMinimal,
} from '../types';
import {
  MEETING_STATUS_LABELS,
  MEETING_TYPE_LABELS,
} from '../types';

type FieldErrors = Partial<Record<keyof FormState | 'non_field', string>>;

const isValidHttpUrl = (raw: string): boolean => {
  const trimmed = raw.trim();
  if (!trimmed) return true;
  try {
    const url = new URL(trimmed);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const validateForm = (form: FormState): FieldErrors => {
  const errors: FieldErrors = {};

  if (!form.title.trim()) {
    errors.title = 'Title is required.';
  }

  if (!form.scheduled_at) {
    errors.scheduled_at = 'Date and time are required.';
  } else if (Number.isNaN(new Date(form.scheduled_at).getTime())) {
    errors.scheduled_at = 'Enter a valid date and time.';
  }

  if (
    form.duration_minutes === null ||
    form.duration_minutes === undefined ||
    Number.isNaN(form.duration_minutes) ||
    form.duration_minutes < 0
  ) {
    errors.duration_minutes = 'Duration must be a positive number.';
  }

  if (form.meeting_link && !isValidHttpUrl(form.meeting_link)) {
    errors.meeting_link =
      'Enter a valid URL starting with http:// or https://, or leave blank.';
  }

  return errors;
};

/** Maps an axios-style error from `requests.ts` into per-field messages. */
const parseServerErrors = (error: unknown): FieldErrors => {
  const out: FieldErrors = {};
  const data = (error as { data?: unknown })?.data;
  if (!data || typeof data !== 'object') {
    out.non_field =
      error instanceof Error ? error.message : 'Failed to save meeting.';
    return out;
  }

  if ('detail' in data && typeof (data as { detail: unknown }).detail === 'string') {
    out.non_field = (data as { detail: string }).detail;
  }

  const formKeys: (keyof FormState)[] = [
    'title',
    'meeting_type',
    'status',
    'scheduled_at',
    'duration_minutes',
    'location',
    'meeting_link',
    'agenda',
    'summary',
    'decisions',
    'action_items',
    'attendees_external',
    'organizer_id',
    'attendees_internal_ids',
  ];
  const aliases: Record<string, keyof FormState> = {
    organizer: 'organizer_id',
    attendees_internal: 'attendees_internal_ids',
  };

  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    const target =
      (formKeys as string[]).includes(key)
        ? (key as keyof FormState)
        : aliases[key];
    if (!target) continue;
    if (Array.isArray(value)) {
      out[target] = value.map((v) => String(v)).join(' ');
    } else if (typeof value === 'string') {
      out[target] = value;
    }
  }

  if (
    !out.non_field &&
    Object.keys(out).length === 0 &&
    'non_field_errors' in data
  ) {
    const nfe = (data as { non_field_errors: unknown }).non_field_errors;
    if (Array.isArray(nfe)) out.non_field = nfe.map(String).join(' ');
  }

  return out;
};

const FieldError: React.FC<{ message?: string }> = ({ message }) => {
  if (!message) return null;
  return (
    <p className="flex items-start gap-1 text-xs text-destructive mt-1">
      <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
      <span>{message}</span>
    </p>
  );
};

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: number | undefined;
  meeting?: Meeting | null;
}

interface FormState {
  title: string;
  meeting_type: MeetingType;
  status: MeetingStatus;
  scheduled_at: string;
  duration_minutes: number;
  location: string;
  meeting_link: string;
  agenda: string;
  summary: string;
  decisions: string;
  action_items: string;
  attendees_external: string;
  organizer_id: string;
  attendees_internal_ids: number[];
}

const blankForm = (): FormState => {
  const now = new Date();
  now.setMinutes(0, 0, 0);
  now.setHours(now.getHours() + 1);
  const isoLocal = (() => {
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  })();

  return {
    title: '',
    meeting_type: 'OTHER',
    status: 'SCHEDULED',
    scheduled_at: isoLocal,
    duration_minutes: 60,
    location: '',
    meeting_link: '',
    agenda: '',
    summary: '',
    decisions: '',
    action_items: '',
    attendees_external: '',
    organizer_id: 'none',
    attendees_internal_ids: [],
  };
};

const toLocalInputValue = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const offsetMs = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - offsetMs).toISOString().slice(0, 16);
};

const fullName = (user: UserMinimal): string => {
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return name || user.email;
};

export const MeetingFormDialog: React.FC<Props> = ({
  open,
  onClose,
  projectId,
  meeting,
}) => {
  const { toast } = useToast();
  const { data: users = [] } = useMeetingProjectUsers(projectId);
  const { createItem, updateItem } = useMeetingMutations(projectId);
  const isEdit = !!meeting;

  const [form, setForm] = useState<FormState>(blankForm);
  const [errors, setErrors] = useState<FieldErrors>({});

  const updateField = <K extends keyof FormState>(
    key: K,
    value: FormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key] && !prev.non_field) return prev;
      const next = { ...prev };
      delete next[key];
      delete next.non_field;
      return next;
    });
  };

  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (!meeting) {
      setForm(blankForm());
      return;
    }
    setForm({
      title: meeting.title,
      meeting_type: meeting.meeting_type,
      status: meeting.status,
      scheduled_at: toLocalInputValue(meeting.scheduled_at),
      duration_minutes: meeting.duration_minutes,
      location: meeting.location ?? '',
      meeting_link: meeting.meeting_link ?? '',
      agenda: meeting.agenda ?? '',
      summary: meeting.summary ?? '',
      decisions: meeting.decisions ?? '',
      action_items: meeting.action_items ?? '',
      attendees_external: meeting.attendees_external ?? '',
      organizer_id: meeting.organizer ? String(meeting.organizer.id) : 'none',
      attendees_internal_ids: (meeting.attendees_internal ?? []).map(
        (u) => u.id,
      ),
    });
  }, [meeting, open]);

  const isPending = createItem.isPending || updateItem.isPending;
  const canSubmit = !!projectId && !isPending;

  const toggleAttendee = (userId: number) => {
    setForm((prev) => {
      const exists = prev.attendees_internal_ids.includes(userId);
      return {
        ...prev,
        attendees_internal_ids: exists
          ? prev.attendees_internal_ids.filter((id) => id !== userId)
          : [...prev.attendees_internal_ids, userId],
      };
    });
  };

  const handleSubmit = async () => {
    if (!projectId) return;

    const validationErrors = validateForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast({
        title: 'Please fix the highlighted fields',
        variant: 'destructive',
      });
      return;
    }

    const draft: MeetingDraft = {
      project: projectId,
      title: form.title.trim(),
      meeting_type: form.meeting_type,
      status: form.status,
      scheduled_at: new Date(form.scheduled_at).toISOString(),
      duration_minutes: form.duration_minutes || 0,
      location: form.location.trim(),
      meeting_link: form.meeting_link.trim(),
      agenda: form.agenda,
      summary: form.summary,
      decisions: form.decisions,
      action_items: form.action_items,
      attendees_external: form.attendees_external,
      organizer_id:
        form.organizer_id === 'none' ? null : Number(form.organizer_id),
      attendees_internal_ids: form.attendees_internal_ids,
    };

    try {
      if (isEdit && meeting) {
        await updateItem.mutateAsync({ id: meeting.id, patch: draft });
        toast({
          title: 'Meeting updated',
          description: 'Your changes were saved.',
        });
      } else {
        await createItem.mutateAsync(draft);
        toast({
          title: 'Meeting created',
          description: 'The meeting has been added.',
        });
      }
      setErrors({});
      onClose();
    } catch (error) {
      const serverErrors = parseServerErrors(error);
      setErrors(serverErrors);
      toast({
        title: isEdit ? 'Failed to update meeting' : 'Failed to create meeting',
        description:
          serverErrors.non_field ||
          'The server rejected the submission. See the highlighted fields.',
        variant: 'destructive',
      });
    }
  };

  const errorRingClass = (key: keyof FormState) =>
    errors[key] ? 'border-destructive focus-visible:ring-destructive' : '';

  const meetingTypes = Object.keys(MEETING_TYPE_LABELS) as MeetingType[];
  const meetingStatuses = Object.keys(MEETING_STATUS_LABELS) as MeetingStatus[];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit Meeting' : 'New Meeting'}
          </DialogTitle>
          <DialogDescription>
            Capture meeting details, agenda, attendees, and outcomes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {errors.non_field && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{errors.non_field}</span>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="meeting-title">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="meeting-title"
              value={form.title}
              onChange={(e) => updateField('title', e.target.value)}
              placeholder="e.g. Weekly OAC Coordination"
              aria-invalid={!!errors.title}
              className={errorRingClass('title')}
            />
            <FieldError message={errors.title} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Meeting Type</Label>
              <Select
                value={form.meeting_type}
                onValueChange={(value) =>
                  updateField('meeting_type', value as MeetingType)
                }
              >
                <SelectTrigger className={errorRingClass('meeting_type')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {meetingTypes.map((t) => (
                    <SelectItem key={t} value={t}>
                      {MEETING_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError message={errors.meeting_type} />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select
                value={form.status}
                onValueChange={(value) =>
                  updateField('status', value as MeetingStatus)
                }
              >
                <SelectTrigger className={errorRingClass('status')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {meetingStatuses.map((s) => (
                    <SelectItem key={s} value={s}>
                      {MEETING_STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError message={errors.status} />
            </div>
            <div className="space-y-1.5">
              <Label>Organizer</Label>
              <Select
                value={form.organizer_id}
                onValueChange={(value) => updateField('organizer_id', value)}
              >
                <SelectTrigger className={errorRingClass('organizer_id')}>
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {fullName(u)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError message={errors.organizer_id} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="meeting-when">
                Date &amp; Time <span className="text-destructive">*</span>
              </Label>
              <Input
                id="meeting-when"
                type="datetime-local"
                value={form.scheduled_at}
                onChange={(e) => updateField('scheduled_at', e.target.value)}
                aria-invalid={!!errors.scheduled_at}
                className={errorRingClass('scheduled_at')}
              />
              <FieldError message={errors.scheduled_at} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="meeting-duration">Duration (minutes)</Label>
              <Input
                id="meeting-duration"
                type="number"
                min={0}
                step={15}
                value={form.duration_minutes}
                onChange={(e) =>
                  updateField(
                    'duration_minutes',
                    Math.max(0, Number(e.target.value) || 0),
                  )
                }
                aria-invalid={!!errors.duration_minutes}
                className={errorRingClass('duration_minutes')}
              />
              <FieldError message={errors.duration_minutes} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="meeting-location">Location</Label>
              <Input
                id="meeting-location"
                value={form.location}
                onChange={(e) => updateField('location', e.target.value)}
                placeholder="Job-site trailer, conference room, etc."
                aria-invalid={!!errors.location}
                className={errorRingClass('location')}
              />
              <FieldError message={errors.location} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="meeting-link">Meeting Link (optional)</Label>
            <Input
              id="meeting-link"
              type="url"
              value={form.meeting_link}
              onChange={(e) => updateField('meeting_link', e.target.value)}
              placeholder="https://example.com/meeting"
              aria-invalid={!!errors.meeting_link}
              className={errorRingClass('meeting_link')}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Optional. Must start with <code>http://</code> or{' '}
              <code>https://</code>.
            </p>
            <FieldError message={errors.meeting_link} />
          </div>

          <div className="space-y-1.5">
            <Label>Internal Attendees</Label>
            <div className="rounded-md border max-h-44 overflow-y-auto p-2 space-y-1">
              {users.length === 0 ? (
                <p className="text-sm text-muted-foreground px-2 py-1">
                  No project users available.
                </p>
              ) : (
                users.map((u) => {
                  const checked = form.attendees_internal_ids.includes(u.id);
                  return (
                    <label
                      key={u.id}
                      className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted/50 cursor-pointer text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAttendee(u.id)}
                        className="h-4 w-4"
                      />
                      <span>{fullName(u)}</span>
                      <span className="text-xs text-muted-foreground ml-auto">
                        {u.email}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
            <FieldError message={errors.attendees_internal_ids} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="meeting-attendees-external">
              External Attendees
            </Label>
            <Textarea
              id="meeting-attendees-external"
              value={form.attendees_external}
              onChange={(e) =>
                updateField('attendees_external', e.target.value)
              }
              placeholder="One per line, or comma-separated. e.g. Jane Doe (Owner), john@arch.com"
              rows={3}
              aria-invalid={!!errors.attendees_external}
              className={errorRingClass('attendees_external')}
            />
            <FieldError message={errors.attendees_external} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="meeting-agenda">Agenda</Label>
            <Textarea
              id="meeting-agenda"
              value={form.agenda}
              onChange={(e) => updateField('agenda', e.target.value)}
              placeholder="Bullet list of items to discuss."
              rows={4}
              aria-invalid={!!errors.agenda}
              className={errorRingClass('agenda')}
            />
            <FieldError message={errors.agenda} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="meeting-summary">Summary / Minutes</Label>
            <Textarea
              id="meeting-summary"
              value={form.summary}
              onChange={(e) => updateField('summary', e.target.value)}
              placeholder="Capture what was discussed during the meeting."
              rows={4}
              aria-invalid={!!errors.summary}
              className={errorRingClass('summary')}
            />
            <FieldError message={errors.summary} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="meeting-decisions">Key Decisions</Label>
              <Textarea
                id="meeting-decisions"
                value={form.decisions}
                onChange={(e) => updateField('decisions', e.target.value)}
                placeholder="Decisions made during the meeting."
                rows={3}
                aria-invalid={!!errors.decisions}
                className={errorRingClass('decisions')}
              />
              <FieldError message={errors.decisions} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="meeting-actions">Action Items</Label>
              <Textarea
                id="meeting-actions"
                value={form.action_items}
                onChange={(e) => updateField('action_items', e.target.value)}
                placeholder="Owner — Task — Due date"
                rows={3}
                aria-invalid={!!errors.action_items}
                className={errorRingClass('action_items')}
              />
              <FieldError message={errors.action_items} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || isPending}
          >
            {isEdit ? 'Save Changes' : 'Create Meeting'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
